import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface UiState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  expandedGroups: Record<string, boolean>
}

interface UiActions {
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setTheme: (theme: 'light' | 'dark') => void
  toggleTheme: () => void
  toggleGroup: (scope: string) => void
  setGroupExpanded: (scope: string, expanded: boolean) => void
}

type UiStore = UiState & UiActions

/**
 * UI preferences store — sidebar state, theme, and dropdown groups.
 *
 * Persisted to localStorage so user preferences survive reloads.
 * Used by layout components starting in Change 21.
 * `expandedGroups` tracks which sidebar dropdowns are open (Change 29).
 */
export const useUiStore = create<UiStore>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      theme: 'light',
      expandedGroups: {},

      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),
      toggleGroup: (scope) =>
        set((s) => ({
          expandedGroups: {
            ...s.expandedGroups,
            [scope]: !s.expandedGroups[scope],
          },
        })),
      setGroupExpanded: (scope, expanded) =>
        set((s) => ({
          expandedGroups: {
            ...s.expandedGroups,
            [scope]: expanded,
          },
        })),
    }),
    {
      name: 'qr-ui-storage',
    },
  ),
)
